import { computed, observable, reaction } from 'mobx';
import LeroyManager from '@/store/leroy/LeroyManager';
import { Store } from '@/store/mainStore';
import Logs from '@/store/leroy/Logs';
import { LeroyScene, SessionState } from '@/store/leroy/Leroy';
import { LeoMetadata } from '@leopoly/arize-eu-insole/dist/metadata/metadata-leo';
import { default as VisualizerPrescription } from '@leopoly/arize-eu-insole/dist/visualizer/adapter-client/prescription-types';
import { Visualizer } from '@leopoly/arize-eu-insole/dist/visualizer/visualizer';
import * as Sentry from '@sentry/vue';
import lodash from 'lodash';

export default class LeroyStore {
	@observable currentScene?: LeroyScene = 'LandmarkSelection';
	@observable modalVisible = false;
	@observable isOnscreenPreview = false;
	@observable sessionState: SessionState = {
		newSceneInitialized: false
	};

	public store: Store;
	public leroyManager: LeroyManager;
	public displayTimeout = 0;
	public landmarksChangedRequest = 0;

	@computed get isLandmarkSelection() {
		return this.currentScene === 'LandmarkSelection';
	}

	constructor(store: Store, platformURL: string) {
		this.store = store;
		this.leroyManager = new LeroyManager(this, platformURL);

		reaction(
			() => [store.currentRoute],
			() => {
				if (this.store.currentRoute) {
					this.manageVisibility(this.store.currentRoute);
				}
			}
		);
		reaction(
			() => [this.isOnscreenPreview],
			() => {
				if (this.isOnscreenPreview) {
					this.SetScene('InsoleVisualizer');
				}
			}
		);
	}

	async manageVisibility(routeName: string) {
		// todo Route path not names?
		this.modalVisible = false;
		if (this.displayTimeout) {
			clearTimeout(this.displayTimeout);
			this.displayTimeout = 0;
		}
		const autoEnableAt = ['Results', 'Landmark selection', '/results'];
		this.displayTimeout = setTimeout(() => {
			if (autoEnableAt.includes(this.store.currentRoute)) {
				console.log(this.store.currentRoute);
				this.modalVisible = true;
			}
		}, 1000);
	}

	public setMetadataFromBE(metadata?: string | null) {
		if (!metadata) {
			return;
		}

		this.leroyManager.metadata = JSON.parse(metadata) as LeoMetadata;
		// FIXME DEMOHACK
		// if (this.currentScene !== undefined) {
		// 	throw new Error('Cannot set metadata after scene has been set');
		// }
	}

	public restart(reload: boolean = false) {
		this.modalVisible = false;
		this.currentScene = undefined;
		this.sessionState.newSceneInitialized = false;
		this.leroyManager.metadata = undefined;
		if (reload) {
			this.leroyManager.resetVisualizer();
		}
		Logs.LeroyRequestsLog('RESTART', '');
	}

	public async SetScene(scene: LeroyScene) {
		if (this.currentScene === scene && this.sessionState.newSceneInitialized) {
			return;
		}
		this.sessionState.newSceneInitialized = false;
		let status = false;
		try {
			await this.store.biometric.waitForLatestScans();
			status = await this.leroyManager.setScene(scene);
		} catch (e) {
			Sentry.captureException(e);
		}
		if (status) {
			this.sessionState.newSceneInitialized = true;
			// NOTE: Could set focus to the canvas here
			this.currentScene = scene;
		} else {
			this.store.utils.pushMessage('Error during initializing view');
		}
	}

	public onResetLandmarks() {
		this.leroyManager.visualizer?.resetLandmarks();
		// NOTE: Automatically triggers onLandmarksChanged
	}

	public mergeLandmakChangeMetadataToPreviouslySetMetadata(metadata: LeoMetadata) {
		if (!this.leroyManager.metadata) {
			this.leroyManager.metadata = metadata;
			return;
		}
		if (this.leroyManager.metadata!.left) {
			this.leroyManager.metadata!.left.footScanParameters = metadata.left.footScanParameters;
			this.leroyManager.metadata!.left.dimensions = metadata.left.dimensions;
		} else if (metadata.left) {
			this.leroyManager.metadata!.left = metadata.left;
		}

		if (this.leroyManager.metadata!.right) {
			this.leroyManager.metadata!.right.footScanParameters = metadata.right.footScanParameters;
			this.leroyManager.metadata!.right.dimensions = metadata.right.dimensions;
		} else if (metadata.right) {
			this.leroyManager.metadata!.right = metadata.right;
		}
	}

	public onLandmarksChanged(metadata: LeoMetadata) {
		this.mergeLandmakChangeMetadataToPreviouslySetMetadata(metadata);
		this.store._patient.isDirty = true;
		if (this.landmarksChangedRequest) {
			clearTimeout(this.landmarksChangedRequest);
		}
		this.landmarksChangedRequest = setTimeout(async () => {
			this.store._patient.markAsDirty();
		}, 2500);
	}

	public async onPrescriptionChanged() {
		// update metadata
		let prescription: VisualizerPrescription = this.store.prescription.newPrescription as VisualizerPrescription;
		// FIXME HACK
		prescription = lodash.cloneDeep(prescription);
		prescription.uiContextData.weight.value = this.store._patient.patientUIData.weight.value.toString();
		prescription.uiContextData.weight.unit = this.store._patient.patientUIData.weight.type;

		if (prescription.shellModifications.mortonsExtension.type === 'none') {
			// @ts-ignore
			prescription.shellModifications.mortonsExtension.side = 'none';
		}
		if (prescription.shellModifications.reverseMortonsExtension.type === 'none') {
			// @ts-ignore
			prescription.shellModifications.reverseMortonsExtension.side = 'none';
		}
		// END-OF FIXME HACK
		if (!this.leroyManager.visualizer) {
			this.leroyManager.resetVisualizer();
		}

		this.leroyManager.metadata = await this.leroyManager.visualizer!.updateMetadataFromPrescription(
			prescription,
			this.leroyManager.metadata || {},
			this.store.biometric.leftScanExists,
			this.store.biometric.rightScanExists,
			this.store.biometric.buildVisualizerFootScan('leftscan'),
			this.store.biometric.buildVisualizerFootScan('rightscan')
		);
		if (this.isOnscreenPreview && this.currentScene) {
			void this.leroyManager.applyMetadata();
		} else {
			this.sessionState.newSceneInitialized = false;
		}
	}
}
